/**
 * Klondike — the flagship variant. PURE rules; no React, no DOM, no feel.
 *
 * Layout
 *   - 7 tableau piles; pile p is dealt p+1 cards, only the top face-up.
 *   - 4 foundations (one per suit, built up A→K).
 *   - stock (the 24-card remainder, face-down) + waste.
 *
 * Rules (draw-one by default — kinder for the audience)
 *   - Tableau builds DOWN in ALTERNATING color (red on black, black on red).
 *   - Foundations build UP by suit from the Ace.
 *   - Only a King — or a valid descending-alternating run headed by a King —
 *     may move onto an empty tableau column.
 *   - A face-down tableau card exposed by a move flips face-up.
 *   - Stock → waste draws one card; an empty stock recycles the waste back
 *     (order preserved, all face-down) for another pass. Unlimited passes.
 *   - foundation → tableau is allowed (lets the player undo a hasty send).
 *   - Win = all 52 cards on the foundations.
 *
 * The deal mirrors `fixtures.ts` exactly so the renderer's pre-engine fixture
 * and the live engine agree card-for-card on the same seed.
 */
import { type Card, makeDeck, seededShuffle } from './cards';
import {
  alternates,
  faced,
  findZone,
  locateCard,
  topCard,
  withCards,
  withZones,
  zone,
  zonesOfKind,
} from './helpers';
import type { GameState, Move, SolitaireVariant, Zone } from './types';

const TABLEAU_COUNT = 7;
const FOUNDATION_COUNT = 4;

// ---------------------------------------------------------------------------
// Deal
// ---------------------------------------------------------------------------

export function deal(seed: number): GameState {
  const deck = seededShuffle(makeDeck(), seed);
  const zones: Zone[] = [];
  let i = 0;

  // 7 tableau piles: pile p gets p+1 cards, only the top face-up.
  for (let p = 0; p < TABLEAU_COUNT; p += 1) {
    const cards: Card[] = [];
    for (let n = 0; n <= p; n += 1) {
      cards.push({ ...deck[i++]!, faceUp: n === p });
    }
    zones.push({ id: `tableau-${p}`, kind: 'tableau', cards, col: p });
  }

  // 4 empty foundations.
  for (let f = 0; f < FOUNDATION_COUNT; f += 1) {
    zones.push({ id: `foundation-${f}`, kind: 'foundation', cards: [], col: f });
  }

  // Stock = the rest (face-down); waste empty.
  zones.push({
    id: 'stock',
    kind: 'stock',
    cards: deck.slice(i).map((c) => faced(c, false)),
  });
  zones.push({ id: 'waste', kind: 'waste', cards: [] });

  return { variant: 'klondike', zones, moveCount: 0, won: false };
}

// ---------------------------------------------------------------------------
// Rule predicates
// ---------------------------------------------------------------------------

/** May `card` (the run head) land on the tableau pile `dest`? */
function canPlaceOnTableau(card: Card, dest: Zone): boolean {
  const top = topCard(dest);
  if (!top) return card.rank === 13; // only a King to an empty column
  return top.faceUp && alternates(card, top) && top.rank === card.rank + 1;
}

/** May `card` go up onto the foundation pile `dest`? */
function canPlaceOnFoundation(card: Card, dest: Zone): boolean {
  const top = topCard(dest);
  if (!top) return card.rank === 1; // only an Ace starts a foundation
  return top.suit === card.suit && card.rank === top.rank + 1;
}

/**
 * From `src`, is the slice starting at `index` a movable run — i.e. all
 * face-up and descending in alternating color from `index` to the top? A
 * single face-up card is trivially a run.
 */
function isMovableRun(src: Zone, index: number): boolean {
  const cards = src.cards;
  const head = cards[index];
  if (!head || !head.faceUp) return false;
  for (let k = index; k < cards.length - 1; k += 1) {
    const a = cards[k]!;
    const b = cards[k + 1]!;
    if (!b.faceUp || !alternates(a, b) || a.rank !== b.rank + 1) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Legal moves
// ---------------------------------------------------------------------------

export function legalMoves(state: GameState): Move[] {
  const moves: Move[] = [];
  const tableaus = zonesOfKind(state, 'tableau');
  const foundations = zonesOfKind(state, 'foundation');
  const stock = zone(state, 'stock');
  const waste = zone(state, 'waste');

  // Draw / recycle from the stock.
  if (stock.cards.length > 0) {
    moves.push({ kind: 'draw' });
  } else if (waste.cards.length > 0) {
    moves.push({ kind: 'recycle' });
  }

  // Waste top → tableau / foundation.
  const wasteTop = topCard(waste);
  if (wasteTop) {
    for (const f of foundations) {
      if (canPlaceOnFoundation(wasteTop, f)) {
        moves.push({ kind: 'move', from: waste.id, to: f.id, cardId: wasteTop.id });
      }
    }
    for (const t of tableaus) {
      if (canPlaceOnTableau(wasteTop, t)) {
        moves.push({ kind: 'move', from: waste.id, to: t.id, cardId: wasteTop.id });
      }
    }
  }

  // Tableau tops → foundation (single card only).
  for (const t of tableaus) {
    const top = topCard(t);
    if (!top || !top.faceUp) continue;
    for (const f of foundations) {
      if (canPlaceOnFoundation(top, f)) {
        moves.push({ kind: 'move', from: t.id, to: f.id, cardId: top.id });
      }
    }
  }

  // Tableau runs → other tableaus.
  for (const src of tableaus) {
    for (let i = 0; i < src.cards.length; i += 1) {
      const head = src.cards[i]!;
      if (!head.faceUp) continue;
      if (!isMovableRun(src, i)) continue;
      for (const dest of tableaus) {
        if (dest.id === src.id) continue;
        // A King-run that already sits at the bottom of an otherwise-empty
        // column gains nothing from hopping to another empty column — that's a
        // pure no-op shuffle, so don't offer it (a King already on a card, or
        // with face-down cards beneath, is a real, useful move).
        if (dest.cards.length === 0 && i === 0 && head.rank === 13) {
          // i === 0 means the run is the whole pile (nothing beneath) — no-op.
          continue;
        }
        if (canPlaceOnTableau(head, dest)) {
          moves.push({ kind: 'move', from: src.id, to: dest.id, cardId: head.id });
        }
      }
    }
  }

  // Foundation top → tableau (allowed: lets the player walk a send back).
  for (const f of foundations) {
    const top = topCard(f);
    if (!top) continue;
    for (const t of tableaus) {
      if (canPlaceOnTableau(top, t)) {
        moves.push({ kind: 'move', from: f.id, to: t.id, cardId: top.id });
      }
    }
  }

  return moves;
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

export function applyMove(state: GameState, move: Move): GameState {
  switch (move.kind) {
    case 'draw':
      return applyDraw(state);
    case 'recycle':
      return applyRecycle(state);
    case 'move':
    case 'auto':
      return applyTransfer(state, move);
    case 'flip':
      return applyFlip(state, move.zone);
    default:
      throw new Error(`klondike: unsupported move kind "${(move as Move).kind}"`);
  }
}

function applyDraw(state: GameState): GameState {
  const stock = zone(state, 'stock');
  const waste = zone(state, 'waste');
  if (stock.cards.length === 0) throw new Error('klondike: draw from empty stock');
  const drawn = stock.cards[stock.cards.length - 1]!;
  return withZones(state, {
    [stock.id]: withCards(stock, stock.cards.slice(0, -1)),
    [waste.id]: withCards(waste, [...waste.cards, faced(drawn, true)]),
  });
}

function applyRecycle(state: GameState): GameState {
  const stock = zone(state, 'stock');
  const waste = zone(state, 'waste');
  if (stock.cards.length !== 0) throw new Error('klondike: recycle with cards still in stock');
  if (waste.cards.length === 0) throw new Error('klondike: recycle with empty waste');
  // Waste returns to stock preserving its drawn order, so the next pass deals
  // the same sequence again: reverse the face-up waste back to a face-down pile.
  const restored = waste.cards.map((c) => faced(c, false)).reverse();
  return withZones(state, {
    [stock.id]: withCards(stock, restored),
    [waste.id]: withCards(waste, []),
  });
}

function applyFlip(state: GameState, zoneId: string): GameState {
  const z = zone(state, zoneId);
  const top = topCard(z);
  if (!top) throw new Error(`klondike: flip empty zone ${zoneId}`);
  if (top.faceUp) return state; // already face-up — no-op
  const cards = z.cards.slice();
  cards[cards.length - 1] = faced(top, true);
  return withZones(state, { [z.id]: withCards(z, cards) });
}

/** Move a card (and any run above it) between zones, with validation + auto-flip. */
function applyTransfer(
  state: GameState,
  move: Extract<Move, { kind: 'move' | 'auto' }>,
): GameState {
  const cardId = move.cardId;
  const toId = move.to;
  const located = locateCard(state, cardId);
  if (!located) throw new Error(`klondike: card not found ${cardId}`);
  const src = located.zone;
  const index = located.index;
  // 'move' may carry an explicit `from`; trust the located source either way.
  const dest = findZone(state, toId);
  if (!dest) throw new Error(`klondike: dest not found ${toId}`);
  if (src.id === dest.id) throw new Error('klondike: move to same zone');

  const moving = src.cards.slice(index);
  const head = moving[0]!;
  if (!head.faceUp) throw new Error('klondike: cannot move a face-down card');

  // Validate against destination kind.
  if (dest.kind === 'foundation') {
    if (moving.length !== 1) throw new Error('klondike: only one card to a foundation');
    if (!canPlaceOnFoundation(head, dest)) throw new Error('klondike: illegal foundation move');
  } else if (dest.kind === 'tableau') {
    if (!isMovableRun(src, index)) throw new Error('klondike: not a movable run');
    if (!canPlaceOnTableau(head, dest)) throw new Error('klondike: illegal tableau move');
  } else {
    throw new Error(`klondike: cannot move onto a ${dest.kind}`);
  }

  // Detach the run; expose + flip the newly-revealed source card if face-down.
  let srcCards = src.cards.slice(0, index);
  if (srcCards.length > 0) {
    const newTop = srcCards[srcCards.length - 1]!;
    if (!newTop.faceUp) {
      srcCards = srcCards.slice();
      srcCards[srcCards.length - 1] = faced(newTop, true);
    }
  }
  const destCards = [...dest.cards, ...moving];

  const next = withZones(state, {
    [src.id]: withCards(src, srcCards),
    [dest.id]: withCards(dest, destCards),
  });
  next.won = isWon(next);
  return next;
}

// ---------------------------------------------------------------------------
// Win + auto-move
// ---------------------------------------------------------------------------

export function isWon(state: GameState): boolean {
  const foundations = zonesOfKind(state, 'foundation');
  const total = foundations.reduce((n, f) => n + f.cards.length, 0);
  return total === 52;
}

/**
 * The next sensible auto-to-foundation move, or null. Prefers the waste, then
 * tableau tops. Conservative: only sends a card whose same-color "siblings" of
 * one rank lower are already safely up (so we never strand a card the player
 * still needs in the tableau) — except low cards (≤2), always safe to send.
 */
export function autoMove(state: GameState): Move | null {
  const foundations = zonesOfKind(state, 'foundation');
  const candidates: Card[] = [];
  const waste = findZone(state, 'waste');
  const wt = waste && topCard(waste);
  if (wt) candidates.push(wt);
  for (const t of zonesOfKind(state, 'tableau')) {
    const top = topCard(t);
    if (top && top.faceUp) candidates.push(top);
  }

  // Top rank currently on each suit's foundation (0 = none yet).
  const rankBySuit: Partial<Record<Card['suit'], number>> = {};
  for (const f of foundations) {
    const top = topCard(f);
    if (top) rankBySuit[top.suit] = top.rank;
  }
  // A card is "safe" to auto-send when both opposite-color foundations are at
  // least one rank below it — then no tableau card still needs it as a parent.
  const minOppositeRank = (card: Card): number => {
    const oppSuits: Card['suit'][] =
      card.color === 'red' ? ['spades', 'clubs'] : ['hearts', 'diamonds'];
    let min = 13;
    for (const s of oppSuits) min = Math.min(min, rankBySuit[s] ?? 0);
    return min;
  };

  for (const card of candidates) {
    const dest = foundations.find((f) => canPlaceOnFoundation(card, f));
    if (!dest) continue;
    if (card.rank <= 2 || minOppositeRank(card) >= card.rank - 1) {
      return { kind: 'auto', to: dest.id, cardId: card.id };
    }
  }
  return null;
}

export const klondike: SolitaireVariant = {
  id: 'klondike',
  name: 'Klondike',
  deal,
  legalMoves,
  applyMove,
  isWon,
  autoMove,
};
